import { useEffect, useMemo, useState } from 'react'
import userService, { UserAdmin, UserCreateAdmin, UserUpdateAdmin } from '../services/userService'
import CreateUserModal from '../components/CreateUserModal'
import EditUserModal from '../components/EditUserModal'
import EditUserPermissionsModal from '../components/EditUserPermissionsModal'

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

export default function UserManagement({ userInfo }: Props) {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<UserAdmin[]>([])
  const [query, setQuery] = useState('')

  // modals
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [isEditOpen, setEditOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserAdmin | null>(null)
  const [isPermOpen, setPermOpen] = useState(false)
  const [permUser, setPermUser] = useState<UserAdmin | null>(null)

  const filtered = useMemo(() => {
    return [...users].sort((a, b) => a.id - b.id)
  }, [users])

  const load = async () => {
    setLoading(true)
    try {
      const list = await userService.listUsers({ q: query || undefined })
      setUsers(list)
    } catch (e: any) {
      alert(e?.message || '사용자 목록을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleCreate = async (payload: UserCreateAdmin, role: 'admin' | 'user') => {
    // 신규 생성은 기본 활성 사용자로 생성한다.
    const u = await userService.createUser({ ...payload, is_active: true })
    let updated = u
    if (role === 'admin') {
      try {
        const next = new Set<string>(u.permissions || [])
        next.add('admin')
        updated = await userService.setUserPermissions(u.id, Array.from(next))
      } catch (e: any) {
        alert(e?.message || '사용자 생성은 완료되었지만 관리자 권한 부여에 실패했습니다. 권한 메뉴에서 다시 시도하세요.')
      }
    }
    setUsers(prev => [updated, ...prev])
    return updated
  }

  const handleOpenEdit = (u: UserAdmin) => {
    setEditingUser(u)
    setEditOpen(true)
  }

  const handleOpenPerm = (u: UserAdmin) => {
    setPermUser(u)
    setPermOpen(true)
  }

  const handleSaveEdit = async (userId: number, payload: UserUpdateAdmin, isAdmin: boolean) => {
    const target = users.find(u => u.id === userId) || null
    let updated = await userService.updateUser(userId, payload)

    // 역할(관리자/사용자) 변경 반영
    if (target) {
      const isSelf = target.email === userInfo.email
      // 자기 자신에 대한 역할 변경은 막고, 나머지 사용자에 대해서만 admin 플래그를 조정
      if (!isSelf) {
        const currentPerms = new Set<string>(target.permissions || [])
        if (isAdmin) {
          currentPerms.add('admin')
        } else {
          currentPerms.delete('admin')
        }
        try {
          const updatedWithPerms = await userService.setUserPermissions(target.id, Array.from(currentPerms))
          updated = updatedWithPerms
        } catch (e: any) {
          // 프로필 정보는 이미 저장되었으므로, 역할 변경 실패만 안내
          alert(e?.message || '역할(관리자/사용자) 변경에 실패했습니다. 다른 정보는 저장되었습니다.')
        }
      }
    }

    setUsers(prev => prev.map(u => (u.id === userId ? updated : u)))
    return updated
  }

  const handleDelete = async (userId: number) => {
    if (!window.confirm('정말로 삭제하시겠습니까?')) return
    try {
      await userService.deleteUser(userId)
      setUsers(prev => prev.filter(u => u.id !== userId))
    } catch (e: any) {
      alert(e?.message || '사용자 삭제 실패')
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">사용자 관리</h1>
        <p className="text-gray-600">등록된 사용자를 조회/추가/수정/삭제합니다.</p>
      </div>

      <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div className="flex-1 bg-white">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                className="px-3 py-2 border border-gray-300 rounded w-64"
                placeholder="검색 (이메일/이름/아이디)"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !loading) { e.preventDefault(); load() } }}
              />
              <button type="button" onClick={load} disabled={loading} className="px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-60">검색</button>
            </div>
            <button onClick={() => setCreateOpen(true)} className="px-3 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-900">+ 사용자 추가</button>
          </div>

          <div className="p-4">
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 border-b">번호</th>
                    <th className="text-left px-3 py-2 border-b">아이디</th>
                    <th className="text-left px-3 py-2 border-b">이메일</th>
                    <th className="text-left px-3 py-2 border-b">이름</th>
                    <th className="text-left px-3 py-2 border-b">권한</th>
                    <th className="text-left px-3 py-2 border-b">액션</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, idx) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 border-b">{idx + 1}</td>
                      <td className="px-3 py-2 border-b">{u.username}</td>
                      <td className="px-3 py-2 border-b">{u.email}</td>
                      <td className="px-3 py-2 border-b">{u.full_name}</td>
                      <td className="px-3 py-2 border-b">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${u.permissions.includes('admin') ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {u.permissions.includes('admin') ? '관리자' : '사용자'}
                        </span>
                      </td>
                      <td className="px-3 py-2 border-b">
                        {(() => {
                          const isSelf = u.email === userInfo.email
                          return (
                            <>
                              <button
                                onClick={() => handleOpenEdit(u)}
                                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 mr-2"
                              >
                                수정
                              </button>
                              {!isSelf && (
                                <button
                                  onClick={() => handleOpenPerm(u)}
                                  className="px-2 py-1 text-xs border border-indigo-300 text-indigo-700 rounded bg-white hover:bg-indigo-50 mr-2"
                                >
                                  권한
                                </button>
                              )}
                              {!isSelf && (
                                <button
                                  onClick={() => handleDelete(u.id)}
                                  className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded bg-white hover:bg-red-50"
                                >
                                  삭제
                                </button>
                              )}
                            </>
                          )
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && <div className="p-3 text-sm text-gray-500">불러오는 중...</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-3 text-sm text-gray-500">사용자가 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateUserModal isOpen={isCreateOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} />
      <EditUserModal
        isOpen={isEditOpen}
        user={editingUser}
        canEditRole={!!(editingUser && editingUser.email !== userInfo.email)}
        onClose={() => { setEditOpen(false); setEditingUser(null) }}
        onSubmit={handleSaveEdit}
      />
      <EditUserPermissionsModal
        isOpen={isPermOpen}
        user={permUser}
        onClose={() => { setPermOpen(false); setPermUser(null) }}
        onUpdated={(updated) => {
          setUsers(prev => prev.map(u => (u.id === updated.id ? updated : u)))
        }}
      />
    </div>
  )
}


